/**
 * Account Detail Page
 */

import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { formatPhoneNumber } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { formatDate, formatCurrency } from '@/lib/utils';
import {
  ArrowLeft, Building2, Users, Briefcase, Ticket, Globe, Phone,
  Pencil, Plus, Trash2, Mail, Star, MapPin,
} from 'lucide-react';

interface Account {
  id: string;
  name: string;
  industry: string | null;
  website: string | null;
  phone: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  country: string;
  status: string;
  healthScore: number | null;
  annualRevenue: number | null;
  employeeCount: number | null;
  ownerName: string | null;
  contactCount: number;
  dealCount: number;
  openTicketCount: number;
  createdAt: string;
}

interface Contact {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  mobile: string | null;
  jobTitle: string | null;
  department: string | null;
  isPrimary: boolean;
  isActive: boolean;
}

const emptyContactForm = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  mobile: '',
  jobTitle: '',
  department: '',
  isPrimary: false,
};

export function AccountDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [contactForm, setContactForm] = useState(emptyContactForm);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['account', id],
    queryFn: async () => {
      const result = await api.getAccount(id!);
      return result;
    },
    enabled: !!id,
  });

  const { data: contactsData } = useQuery({
    queryKey: ['contacts', { accountId: id }],
    queryFn: async () => {
      const result = await api.getContacts({ accountId: id!, limit: 100 });
      return result;
    },
    enabled: !!id,
  });

  const account = data?.data as Account | undefined;
  const contacts = (contactsData?.data || []) as Contact[];

  const createContactMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => api.createContact(data as Parameters<typeof api.createContact>[0]),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts', { accountId: id }] });
      queryClient.invalidateQueries({ queryKey: ['account', id] });
      closeContactDialog();
    },
  });

  const updateContactMutation = useMutation({
    mutationFn: ({ contactId, data }: { contactId: string; data: Record<string, unknown> }) =>
      api.updateContact(contactId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts', { accountId: id }] });
      closeContactDialog();
    },
  });

  const deleteContactMutation = useMutation({
    mutationFn: (contactId: string) => api.deleteContact(contactId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts', { accountId: id }] });
      queryClient.invalidateQueries({ queryKey: ['account', id] });
    },
  });

  const deleteAccountMutation = useMutation({
    mutationFn: () => api.deleteAccount(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      navigate('/customer-operations/accounts');
    },
  });

  function openCreateContact() {
    setEditingContact(null);
    setContactForm(emptyContactForm);
    setContactDialogOpen(true);
  }

  function openEditContact(contact: Contact) {
    setEditingContact(contact);
    setContactForm({
      firstName: contact.firstName,
      lastName: contact.lastName,
      email: contact.email,
      phone: contact.phone || '',
      mobile: contact.mobile || '',
      jobTitle: contact.jobTitle || '',
      department: contact.department || '',
      isPrimary: contact.isPrimary,
    });
    setContactDialogOpen(true);
  }

  function closeContactDialog() {
    setContactDialogOpen(false);
    setEditingContact(null);
    setContactForm(emptyContactForm);
  }

  function handleContactSubmit(e: React.FormEvent) {
    e.preventDefault();
    const payload = {
      firstName: contactForm.firstName,
      lastName: contactForm.lastName,
      email: contactForm.email,
      phone: contactForm.phone || null,
      mobile: contactForm.mobile || null,
      jobTitle: contactForm.jobTitle || null,
      department: contactForm.department || null,
      isPrimary: contactForm.isPrimary,
    };

    if (editingContact) {
      updateContactMutation.mutate({ contactId: editingContact.id, data: payload });
    } else {
      createContactMutation.mutate({ accountId: id!, ...payload } as Parameters<typeof api.createContact>[0]);
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!account) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-medium mb-2">Account not found</h2>
        <Button variant="outline" onClick={() => navigate('/customer-operations/accounts')}>
          Back to Accounts
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/customer-operations/accounts')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">{account.name}</h1>
          <p className="text-muted-foreground">
            {account.industry || 'No industry'}
            {account.city && ` • ${account.city}, ${account.state}`}
          </p>
        </div>
        <Button variant="outline" onClick={() => navigate('edit')}>
          <Pencil className="h-4 w-4 mr-2" />
          Edit
        </Button>
        <Button variant="outline" className="text-destructive hover:bg-destructive hover:text-destructive-foreground" onClick={() => setDeleteDialogOpen(true)}>
          <Trash2 className="h-4 w-4 mr-2" />
          Delete
        </Button>
        <span
          className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
            account.status === 'active'
              ? 'bg-green-100 text-green-800'
              : account.status === 'prospect'
              ? 'bg-blue-100 text-blue-800'
              : 'bg-gray-100 text-gray-800'
          }`}
        >
          {account.status}
        </span>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{account.contactCount}</p>
                <p className="text-sm text-muted-foreground">Contacts</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Briefcase className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{account.dealCount}</p>
                <p className="text-sm text-muted-foreground">Deals</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Ticket className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{account.openTicketCount}</p>
                <p className="text-sm text-muted-foreground">Open Tickets</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Building2 className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {account.healthScore !== null ? `${account.healthScore}%` : 'N/A'}
                </p>
                <p className="text-sm text-muted-foreground">Health Score</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Details + Contacts */}
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Account Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {account.website && (
              <div className="flex items-center gap-3">
                <Globe className="h-4 w-4 text-muted-foreground" />
                <a
                  href={account.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  {account.website}
                </a>
              </div>
            )}
            <div className="flex items-center gap-3">
              <Phone className="h-4 w-4 text-muted-foreground" />
              <span>{account.phone ? formatPhoneNumber(account.phone) : <span className="text-muted-foreground italic">No phone on file</span>}</span>
            </div>
            <div className="flex items-start gap-3">
              <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
              {account.addressLine1 ? (
                <div className="text-sm">
                  <p>{account.addressLine1}</p>
                  {account.addressLine2 && <p>{account.addressLine2}</p>}
                  <p>
                    {[account.city, account.state].filter(Boolean).join(', ')}
                    {account.postalCode && ` ${account.postalCode}`}
                  </p>
                  {account.country && account.country !== 'USA' && <p>{account.country}</p>}
                </div>
              ) : (
                <span className="text-muted-foreground italic text-sm">No address on file</span>
              )}
            </div>
            <div className="pt-4 border-t space-y-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Annual Revenue</span>
                <span className="font-medium">
                  {account.annualRevenue ? formatCurrency(account.annualRevenue) : 'N/A'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Employees</span>
                <span className="font-medium">{account.employeeCount || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Owner</span>
                <span className="font-medium">{account.ownerName || 'Unassigned'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Created</span>
                <span className="font-medium">{formatDate(account.createdAt)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Contacts */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Contacts</CardTitle>
            <Button variant="outline" size="sm" onClick={openCreateContact}>
              <Plus className="h-4 w-4 mr-1" />
              Add Contact
            </Button>
          </CardHeader>
          <CardContent>
            {contacts.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                No contacts yet. Add your first contact.
              </p>
            ) : (
              <div className="space-y-3">
                {contacts.map((contact) => (
                  <div
                    key={contact.id}
                    className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium">
                        {contact.firstName[0]}{contact.lastName[0]}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm">
                            {contact.firstName} {contact.lastName}
                          </span>
                          {contact.isPrimary && (
                            <Badge variant="secondary" className="text-xs px-1.5 py-0">
                              <Star className="h-3 w-3 mr-0.5" />
                              Primary
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            {contact.email}
                          </span>
                          {contact.jobTitle && <span>{contact.jobTitle}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditContact(contact)}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-muted-foreground hover:text-destructive"
                        onClick={() => {
                          if (confirm('Delete this contact?')) {
                            deleteContactMutation.mutate(contact.id);
                          }
                        }}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Contact Dialog */}
      <Dialog open={contactDialogOpen} onOpenChange={setContactDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingContact ? 'Edit Contact' : 'Add Contact'}</DialogTitle>
            <DialogDescription>
              {editingContact ? 'Update contact information.' : 'Add a new contact to this account.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleContactSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>First Name *</Label>
                <Input
                  value={contactForm.firstName}
                  onChange={(e) => setContactForm({ ...contactForm, firstName: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Last Name *</Label>
                <Input
                  value={contactForm.lastName}
                  onChange={(e) => setContactForm({ ...contactForm, lastName: e.target.value })}
                  required
                />
              </div>
            </div>

            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={contactForm.email}
                onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Phone</Label>
                <Input
                  value={contactForm.phone}
                  onChange={(e) => setContactForm({ ...contactForm, phone: formatPhoneNumber(e.target.value) })}
                  placeholder="xxx-xxx-xxxx"
                  maxLength={12}
                />
              </div>
              <div>
                <Label>Mobile</Label>
                <Input
                  value={contactForm.mobile}
                  onChange={(e) => setContactForm({ ...contactForm, mobile: formatPhoneNumber(e.target.value) })}
                  placeholder="xxx-xxx-xxxx"
                  maxLength={12}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Job Title</Label>
                <Input
                  value={contactForm.jobTitle}
                  onChange={(e) => setContactForm({ ...contactForm, jobTitle: e.target.value })}
                />
              </div>
              <div>
                <Label>Department</Label>
                <Input
                  value={contactForm.department}
                  onChange={(e) => setContactForm({ ...contactForm, department: e.target.value })}
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="isPrimary"
                checked={contactForm.isPrimary}
                onChange={(e) => setContactForm({ ...contactForm, isPrimary: e.target.checked })}
                className="rounded border-gray-300"
              />
              <Label htmlFor="isPrimary" className="mb-0">Primary contact</Label>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeContactDialog}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={createContactMutation.isPending || updateContactMutation.isPending}
              >
                {(createContactMutation.isPending || updateContactMutation.isPending)
                  ? 'Saving...'
                  : editingContact ? 'Update Contact' : 'Add Contact'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Account Confirmation */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Account</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{account.name}</strong>? This will permanently remove the account and all associated invoices, payments, contracts, and recurring schedules. Contacts and deals will also be deleted. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteAccountMutation.mutate()}
              disabled={deleteAccountMutation.isPending}
            >
              {deleteAccountMutation.isPending ? 'Deleting...' : 'Delete Account'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
